import { log } from '@/log'
import ThumbnailUrl from '@/models/item/thumbnail-url'
import { ContactLensImageType } from '@/services/contact-lens-image-type/contact-lens-image-type-storage'
import { ColoredContactLensFkuItem, FkuItemBundleDiscount } from '@hl-itemlist/api/itemlist-api'
import { ExpirationPeriod, expirationPeriodOptions } from '@hl-itemlist/model/condition'

/**
 * 一覧のカード1枚分の表示モデル。APIレスポンス(FKU)を表示に必要な形へこの境界で変換する。
 */
export interface ProductCardImages {
  readonly thumbnailUrl: string
  readonly lensImageUrl: string
  readonly modelImageUrl: string
}

export interface ProductCard {
  readonly id: number
  readonly name: string
  readonly colorName: string
  readonly images: ProductCardImages
  /**
   * 商品画像に重ねて表示する背景透過のオーバーレイ画像(任意登録)。未登録はnull(何も重ねない)。
   * 内容は運用登録で「売り切れ」「残りわずか」等のバンドが入るが、フロントからは意味を特定できない
   * (このため代替テキストも付けられない。ProductCardテンプレートのコメント参照)。
   * 選択中の画像種別(モデル/レンズ/着画)に依らず常に重ねる(iOSアプリの一覧セルFkuItemCellViewと同じ仕様)。
   */
  readonly layerImageUrl: string | null
  readonly href: string
  /**
   * スペック表の先頭セルに表示する装用期間。nullは「既知の語彙で表せない」を表し、セルごと非表示にする
   * (toExpirationPeriodSpecのコメント参照)。
   */
  readonly expirationPeriod: ExpirationPeriodSpec | null
  readonly specs: string[]
  /**
   * 商品が属するまとめて割引グループのラベル色(グループごとに1つ)。対象外は空配列。
   * 色が未設定(空文字。BFF へ同期前のレコード)のグループは既定色に倒す: ラベルの有無が「対象かどうか」の主情報で、
   * 色はグループ判別の補助のため、色が無いからといってラベルごと消さない。
   */
  readonly bundleDiscountColors: ReadonlyArray<string>
}

/**
 * まとめて割引の色が未設定のときの既定色。白文字が読める無彩色にし、値引き率バッジ(赤)と混同されない色にしている。
 */
export const DEFAULT_BUNDLE_DISCOUNT_COLOR = '#757575'

/**
 * スペック表の先頭セルに表示する装用期間。
 * 期間ごとにセルを色分けするため、表示ラベルだけでなくAPIの値も保持する。
 */
export interface ExpirationPeriodSpec {
  readonly value: ExpirationPeriod
  readonly label: string
}

/**
 * gpRootId は `...productclass-<shortId>` という形式で、末尾のshortIdが商品詳細URLの識別子。
 * 形式が想定と異なる場合はそのまま使う(リンク切れよりは遷移を試みる方に倒す)。
 */
const ROOT_SHORT_ID_MARKER = 'productclass-'

export function toProductCard(item: ColoredContactLensFkuItem): ProductCard {
  // colorクエリで商品詳細ページの初期表示カラーをこのFKUに合わせる
  const colorQuery = item.colorCode ? `?color=${encodeURIComponent(item.colorCode)}` : ''

  return {
    id: item.id,
    name: item.root.productName,
    colorName: item.colorName,
    // BFFは元サイズのCloudFront URLだけを返し、suffix付きURLでリサイズ版へアクセスするのが上流の契約
    // (pj-hotel-lovers-api docs/images.md)。一覧グリッドはiOSアプリの一覧セルと同じ`_s`(一覧向けサイズ)を使う
    images: {
      thumbnailUrl: ThumbnailUrl.asSizeS(item.thumbnailUrl).value,
      lensImageUrl: ThumbnailUrl.asSizeS(item.lensImageUrl).value,
      modelImageUrl: ThumbnailUrl.asSizeS(item.modelImageUrl).value,
    },
    layerImageUrl: item.layerImageUrl !== null ? ThumbnailUrl.asSizeS(item.layerImageUrl).value : null,
    // hl2はSINGLEインスタンスのホスト直下でのみ動くため、商品詳細へのパスにブランドprefixは不要
    href: `/item/${extractRootShortId(item.root.gpRootId)}${colorQuery}`,
    expirationPeriod: toExpirationPeriodSpec(item.expirationPeriod),
    specs: buildSpecs(item),
    bundleDiscountColors: item.bundleDiscounts.map(toBundleDiscountColor),
  }
}

function toBundleDiscountColor(bundleDiscount: FkuItemBundleDiscount): string {
  return bundleDiscount.labelColorHex === '' ? DEFAULT_BUNDLE_DISCOUNT_COLOR : bundleDiscount.labelColorHex
}

/**
 * 装用期間をスペック表のセル表示用に解決する。
 * 語彙(oneDay/twoWeeks/oneMonth)の外の値は、上流BFFの契約拡張(新しい装用期間の追加)でありうるため
 * API境界ではエラーにせず、ここでnullに落として期間セルだけ非表示にする
 * (一覧・商品は表示し続け、enum値を生のままユーザーに見せない)。拡張に気づけるようログには記録する。
 */
function toExpirationPeriodSpec(period: string): ExpirationPeriodSpec | null {
  const option = expirationPeriodOptions.find(item => item.value === period)

  if (!option) {
    log.warn(`hl-itemlist: unknown expirationPeriod: ${period}`)

    return null
  }

  return { value: option.value, label: option.label }
}

/**
 * カードのスペック表に表示する装用期間以外のスペック一覧(表示順込み)。
 * サイズのlabelは "14.4mm" のような値なので、種別のprefix(DIA/着色/BC)を付けて表記する。
 * 水光系は該当する場合のみ、より具体的な「回らない水光」を優先して片方だけ表記する。
 */
function buildSpecs(item: ColoredContactLensFkuItem): string[] {
  const specs: string[] = [
    `DIA ${item.diameter.label}`,
    `着色 ${item.graphicDiameter.label}`,
    `BC ${item.baseCurve.label}`,
  ]

  if (item.isNonRotatingSuikou) {
    specs.push('回らない水光')
  } else if (item.isSuikou) {
    specs.push('水光')
  }
  if (item.isSilicon) {
    specs.push('シリコーン')
  }

  return specs
}

/**
 * 選択された画像種別の表示URLを解決する。
 * 種別とURLの対応: 着画(worn)=modelImageUrl / レンズ(lens)=lensImageUrl / パッケージ(package)=thumbnailUrl。
 * 一覧APIはspec完全な商品だけを返す契約のため各URLはnon-null(API境界でstring型を検証)だが、
 * 空文字までは契約・検証とも禁止していない。空だった場合の表示はカード側の防御が受け持つ
 * (ProductCardの画像のv-elseのコメント参照)。
 */
export function imageUrlFor(images: ProductCardImages, type: ContactLensImageType): string {
  return (
    type === 'worn' ? images.modelImageUrl :
    type === 'lens' ? images.lensImageUrl :
    images.thumbnailUrl
  )
}

function extractRootShortId(gpRootId: string): string {
  const index = gpRootId.lastIndexOf(ROOT_SHORT_ID_MARKER)

  return index >= 0 ? gpRootId.slice(index + ROOT_SHORT_ID_MARKER.length) : gpRootId
}
